import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import { DatePickerField } from '@spark/ui-components/components/form/date-pickers/DatePicker';
import { pickFirstValue } from '../../shared/controleDescargaMapper';
import { toApiDateTime } from './descargaDateTime';

const isEmptyDisplayValue = (value) => value === null || value === undefined || value === '' || value === '-';

const toLocalDate = (value) => {
  if (!value || value === '-') return null;
  if (value instanceof Date) return value;

  const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
  if (!parts) return null;

  return new Date(parts[1], Number(parts[2]) - 1, parts[3], parts[4], parts[5]);
};

const buildInitialValues = (row) => ({
  numeroCarga: isEmptyDisplayValue(row?.numeroCarga) ? '' : String(row?.numeroCarga),
  frota: isEmptyDisplayValue(row?.numeroFrota) ? '' : String(row?.numeroFrota),
  gestor: isEmptyDisplayValue(row?.nomeResponsavel) ? '' : String(row?.nomeResponsavel),
  loja: isEmptyDisplayValue(row?.loja) ? '' : String(row?.loja),
  cidade: isEmptyDisplayValue(row?.cidade) ? '' : String(row?.cidade),
  uf: isEmptyDisplayValue(row?.ufDestinatario) ? '' : String(row?.ufDestinatario),
  reboques: isEmptyDisplayValue(row?.frotasReboque) ? '' : String(row?.frotasReboque),
  dataAgendamento: toLocalDate(row?.dataAgendamentoDescargaOriginal),
  dataChegada: toLocalDate(row?.chegadaDescargaOriginal),
  dataSaida: toLocalDate(row?.saidaDescargaOriginal),
  observacoes: isEmptyDisplayValue(row?.observacoesDescargaOriginal ?? row?.observacoesDescarga)
    ? ''
    : String(row?.observacoesDescargaOriginal ?? row?.observacoesDescarga)
});

const DescargaManualFormModal = ({
  show,
  mode,
  row,
  loading,
  onClose,
  onSubmit
}) => {
  const isEdit = mode === 'edit';
  const [values, setValues] = useState(buildInitialValues(null));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) return;
    setValues(buildInitialValues(row));
    setErrors({});
  }, [show, row]);

  const titulo = useMemo(() => {
    if (isEdit) return 'Editar descarga manual';
    return 'Nova descarga manual';
  }, [isEdit]);

  const handleChange = (field) => (event) => {
    setValues((current) => ({
      ...current,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (value) => {
    setValues((current) => ({
      ...current,
      [field]: value
    }));
  };

  const validar = () => {
    const nextErrors = {};

    if (!String(values.numeroCarga || '').trim()) nextErrors.numeroCarga = 'Informe o numero da carga.';
    if (!String(values.frota || '').trim()) nextErrors.frota = 'Informe a frota.';
    if (!String(values.gestor || '').trim()) nextErrors.gestor = 'Informe o gestor.';
    if (!values.dataAgendamento) nextErrors.dataAgendamento = 'Informe a data de agendamento.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validar()) return;

    onSubmit({
      numeroCarga: String(values.numeroCarga).trim(),
      frota: String(values.frota).trim(),
      gestor: String(values.gestor).trim(),
      dataAgendamento: toApiDateTime(values.dataAgendamento),
      loja: String(values.loja || '').trim() || null,
      cidade: String(values.cidade || '').trim() || null,
      uf: String(values.uf || '').trim().toUpperCase() || null,
      reboques: String(values.reboques || '').trim() || null,
      dataChegada: toApiDateTime(values.dataChegada),
      dataSaida: toApiDateTime(values.dataSaida),
      observacoes: String(values.observacoes || '').trim() || null
    });
  };

  const manualId = pickFirstValue(row, ['descargaManualId', 'DescargaManualId']);

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop={loading ? 'static' : true}>
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{titulo}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {isEdit ? (
            <ManualBadge>Descarga manual #{manualId || '-'}</ManualBadge>
          ) : null}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numero da carga *</Form.Label>
                <Form.Control
                  value={values.numeroCarga}
                  onChange={handleChange('numeroCarga')}
                  disabled={loading}
                  isInvalid={!!errors.numeroCarga}
                />
                <Form.Control.Feedback type="invalid">{errors.numeroCarga}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Frota *</Form.Label>
                <Form.Control
                  value={values.frota}
                  onChange={handleChange('frota')}
                  disabled={loading}
                  isInvalid={!!errors.frota}
                />
                <Form.Control.Feedback type="invalid">{errors.frota}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Gestor *</Form.Label>
                <Form.Control
                  value={values.gestor}
                  onChange={handleChange('gestor')}
                  disabled={loading}
                  isInvalid={!!errors.gestor}
                />
                <Form.Control.Feedback type="invalid">{errors.gestor}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Loja</Form.Label>
                <Form.Control value={values.loja} onChange={handleChange('loja')} disabled={loading} />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cidade</Form.Label>
                <Form.Control value={values.cidade} onChange={handleChange('cidade')} disabled={loading} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>UF</Form.Label>
                <Form.Control
                  value={values.uf}
                  onChange={handleChange('uf')}
                  disabled={loading}
                  maxLength={2}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Reboques</Form.Label>
                <Form.Control value={values.reboques} onChange={handleChange('reboques')} disabled={loading} />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <DatePickerField
                  label="Data agendamento"
                  type="datetime"
                  value={values.dataAgendamento}
                  onChange={handleDateChange('dataAgendamento')}
                  disabled={loading}
                  required
                  error={errors.dataAgendamento}
                  inputClassName="form-control"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <DatePickerField
                  label="Chegada"
                  type="datetime"
                  value={values.dataChegada}
                  onChange={handleDateChange('dataChegada')}
                  disabled={loading}
                  inputClassName="form-control"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <DatePickerField
                  label="Saida"
                  type="datetime"
                  value={values.dataSaida}
                  onChange={handleDateChange('dataSaida')}
                  disabled={loading}
                  inputClassName="form-control"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-0">
            <Form.Label>Observacoes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={values.observacoes}
              onChange={handleChange('observacoes')}
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <ButtonWithLoading variant="outline-secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </ButtonWithLoading>
          <ButtonWithLoading type="submit" variant="primary" loading={loading} loadingText="Salvando...">
            Salvar
          </ButtonWithLoading>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

DescargaManualFormModal.propTypes = {
  show: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  row: PropTypes.object,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

DescargaManualFormModal.defaultProps = {
  row: null,
  loading: false
};

export default DescargaManualFormModal;

const ManualBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff3cd;
  color: #856404;
  font-size: 0.85rem;
  font-weight: 600;
`;
